import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, Check, ArrowLeft } from 'lucide-react';
import moment from 'moment';

/**
 * The Called tile on a property's Enquiries / Attendees / Buyers rows
 * (DOMA 13307134618, then its follow-up improvements).
 *
 * A tap opens a small menu: log the call OUTCOME (so the note reads like the
 * ones agents already write — "no answer", "left voicemail"), or set a quick
 * CALL-BACK reminder that surfaces in Who-to-Call. Once the person has been
 * contacted the tile turns green-on-white and reads "Called {ago}", so an agent
 * ringing down an inspection register can see who they have already done.
 *
 * "Spoke to them" opens a notepad first (Paul Jimoulis, CRM-2026-00110): the
 * agent types what the conversation was about and it is saved with the call,
 * with an optional call-back reminder. Leaving the pad empty still logs the call.
 */

const SPOKE_OUTCOME = 'Called, spoke to them';

const OUTCOMES = [
  { label: 'Spoke to them', outcome: SPOKE_OUTCOME, withNote: true },
  { label: 'No answer', outcome: 'Called, no answer' },
  { label: 'Left voicemail', outcome: 'Called, left a voicemail' },
  { label: 'Wrong number', outcome: 'Called, wrong number' },
];

const CALLBACKS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

// Compact "how long ago", e.g. today / 2d / 3w / 2mo. Pure arithmetic (no date
// formatting), so it stays out of the agency timezone lint rule.
const compactAgo = (iso) => {
  if (!iso) return '';
  const d = moment(iso);
  if (!d.isValid()) return '';
  const days = moment().diff(d, 'days');
  if (days <= 0) return 'today';
  if (days < 7) return `${days}d`;
  if (days < 31) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
};

// Per-tab sizing so the tile matches the row it sits in.
const SIZES = {
  enquiry: { pad: 'text-xs px-2.5 sm:px-3 py-2', label: 'hidden sm:inline' },
  attendee: { pad: 'text-xs px-3 py-2', label: '' },
  buyer: { pad: 'text-[11px] px-3 py-2', label: '' },
};

export default function CalledButton({ lastContacted, busy = false, onCalled, size = 'attendee' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  // The "Spoke to them" notepad: typed note + optional call-back (days).
  const [noteMode, setNoteMode] = useState(false);
  const [note, setNote] = useState('');
  const [noteRemind, setNoteRemind] = useState(0);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  // Many tiles render on one page, so the notepad's field ids are per tile.
  const uid = useId();
  const noteId = `${uid}-note`;
  const remindId = `${uid}-remind`;

  // Close on any outside click or page scroll (the menu is pinned to the
  // viewport). Scrolling INSIDE the menu (a long note in the textarea) must not
  // close it, or the agent loses what they typed.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onScroll = (e) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const right = Math.max(8, window.innerWidth - r.right);
      setPos(
        window.innerHeight - r.bottom > 320
          ? { top: r.bottom + 4, right }
          : { bottom: window.innerHeight - r.top + 4, right },
      );
    }
    setNoteMode(false);
    setNote('');
    setNoteRemind(0);
    setOpen(true);
  };

  const pick = (outcome, remindInDays) => (e) => {
    e.stopPropagation();
    setOpen(false);
    onCalled({ outcome, remindInDays: remindInDays || null });
  };

  const openNotepad = (e) => {
    e.stopPropagation();
    setNoteMode(true);
  };

  const saveSpokeTo = (e) => {
    if (e) e.stopPropagation();
    const typed = note.trim();
    setOpen(false);
    onCalled({
      outcome: typed ? `${SPOKE_OUTCOME}: ${typed}` : SPOKE_OUTCOME,
      remindInDays: noteRemind || null,
    });
  };

  const sz = SIZES[size] || SIZES.attendee;
  const ago = compactAgo(lastContacted);
  const label = busy ? 'Logging…' : ago ? `Called ${ago}` : 'Called';
  const contacted = Boolean(lastContacted);

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        title={contacted ? `Last called ${ago} ago` : 'Log that you called this person'}
        className={`inline-flex items-center gap-1.5 ${
          contacted
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100'
            : 'bg-emerald-500 hover:bg-emerald-600 text-white'
        } disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wider rounded-lg ${sz.pad} transition-colors`}
      >
        <Phone className="w-3.5 h-3.5" /> <span className={sz.label}>{label}</span>
      </button>
      {open && pos && !noteMode && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Log the call"
          className="fixed z-[90] w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-1 text-xs"
          style={pos}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-3 py-1 text-[10px] font-black text-gray-400 uppercase tracking-widest">Log the call</p>
          {OUTCOMES.map((o) => (
            <button
              key={o.label}
              type="button"
              role="menuitem"
              onClick={o.withNote ? openNotepad : pick(o.outcome)}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
            >
              <Phone className="w-3 h-3 text-emerald-500" /> {o.label}
            </button>
          ))}
          <div className="border-t border-gray-100 my-1" />
          <p className="px-3 py-1 text-[10px] font-black text-gray-400 uppercase tracking-widest">Called, call back</p>
          {CALLBACKS.map((c) => (
            <button
              key={c.label}
              type="button"
              role="menuitem"
              onClick={pick('Called, will try again', c.days)}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
            >
              <Check className="w-3 h-3 text-[#55C5D0]" /> {c.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
      {open && pos && noteMode && createPortal(
        <div
          ref={menuRef}
          role="dialog"
          aria-label="Spoke to them"
          className="fixed z-[90] w-72 bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-xs"
          style={pos}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setNoteMode(false); }}
              aria-label="Back to the call menu"
              className="p-1 -ml-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Spoke to them</p>
          </div>
          <label htmlFor={noteId} className="block text-[11px] font-semibold text-gray-600 mb-1">
            Note of the conversation
          </label>
          <textarea
            id={noteId}
            autoFocus
            rows={5}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveSpokeTo(e);
            }}
            placeholder="What did you talk about?"
            className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y"
          />
          <label htmlFor={remindId} className="block text-[11px] font-semibold text-gray-600 mt-2 mb-1">
            Call back reminder
          </label>
          <select
            id={remindId}
            value={noteRemind}
            onChange={(e) => setNoteRemind(Number(e.target.value))}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-gray-800 bg-white"
          >
            <option value={0}>No reminder</option>
            {CALLBACKS.map((c) => (
              <option key={c.days} value={c.days}>{c.label}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveSpokeTo}
              className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
            >
              Save call
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
